/**
 * Websockets handles subscribing to pusher channels so the
 * client can receive User and Tenant messages from the server.
 */

import Tenant from '@/types/Tenant.interface'
import User from '@/types/User.interface'
import { store } from '@/store'
import LaravelEcho from 'laravel-echo'
import moment from 'moment'
import events from '@/events/events'
import Pusher from 'pusher-js'
import AppSync from './AppSync'


//  Define our channel object (we use it craft channel ids)
export interface ChannelProps {
    model: string
    id: number
    timestamp: number
}

//  Create containers to hold each active channel name
let tenantChannel: ChannelProps = null
let userChannel: ChannelProps = null

//  Initialize Pusher and Laravel Echo
//  We have this inside of an init function so that we only have it apply to authenticated users
//  that are currently signed into a user AND a tenant
const init = () => {
    //  Begin pusher initialization
    store.getters.appDebug && console.log('WEBSOCKETS: Initializing Pusher Connection')

    //  Assemble the Laravel Echo authentication endpoint for this environment
    const authUrl = `${process.env.VUE_APP_URL_API}/broadcasting/auth`

    //  Initialize Laravel Echo
    window.Pusher = Pusher
    window.Echo = new LaravelEcho({
        broadcaster: 'pusher',
        key: process.env.VUE_APP_PUSHER_KEY,
        cluster: 'us2',
        encrypted: true,
        forceTLS: true,
        enabledTransports: ['ws', 'wss'],
        authEndpoint: authUrl
    })

    window.Echo.connector.pusher.connection.bind('error', (e) => {
        const status = window.Echo.connector.pusher.connection.state
    
        console.error('PUSHER ERROR', `connection: ${status}`, e)
    });
    window.Echo.connector.pusher.connection.bind('state_change', (states) => {
        store.getters.appDebug && console.log('PUSHER STATE CHANGE', states, moment().format('HH:mm'))
        
        //  Get the current status of the connection to Pusher
        const status = window.Echo.connector.pusher.connection.state
    
        //  This occurs when a browser is incompatible, OR the network is unavailable for an extended time
        const failedConnection = status === 'failed'
    
        //  If the connection is failed, try to reconnect User and Tenant channel
        if (failedConnection) onConnectionFailed()
    
        //  This occurs when the client loses its internet connection
        const failedNetwork  = status === 'connecting' && states.previous === 'unavailable'
    
        //  This occurs when a computer returns from sleeping (ie network was ok, but the client is missing the websocket events published while sleeping)
        const reconnecting = status === 'connecting' && states.previous === 'connected' && !store.getters.isOffline
    
        //  Sometimes it's possible for someone to go straight from 'unavailable' to 'connected' and skip the 'connecting' state
        const edgeCase = status === 'connected' && states.previous === 'unavailable'
    
        //  Determine is the connection is reconnecting, and needs to reload data
        const isReconnecting = failedNetwork || failedConnection || reconnecting || edgeCase
    
        //  If the connection is 'reconnecting', publish the event
        if (isReconnecting) onReconnectedConnection()
    
        //  This occurs when ever a connection is established
        const newConnection = status === 'connected' && states.previous === 'connecting'
    
        //  If this is a new connection, publish the event
        if (newConnection) onNewConnection()
    });
}


//  Initialize the User Notifications channel
const userInit = (user: User) => {
    //  Begin channel initialization
    store.getters.appDebug && console.log('WEBSOCKETS: Initializing User Channel')

    //  If no user exists, throw error
    if (!user) throw 'User websockets required a selected user'

    //  Set the user's channel attributes
    userChannel = {
        model: 'App.SnapDesk.User',
        id: user.id,
        timestamp: moment.utc(user.created_at).unix()
    }

    //  Convert ChannelProps into dot notated string
    const channelStr = _getChannelName(userChannel)

    //  Check if the user channel is already registered in Laravel Echo
    const hasUserChannel = `private-${channelStr}` in window.Echo.connector.channels

    //  If the user channel already exists, do nothing
    if (hasUserChannel) return

    //  Else, subscribe to the User channel with Laravel Echo
    window.Echo.private(channelStr).notification((notification) => {
        store.getters.appDebug && console.log('WEBSOCKET: User Notification Received:', notification)

        switch (notification.type) {
            case 'App\\Notifications\\UserNotification':
                events.onUserNotificationReceived(notification)
                break
            case 'App\\Notifications\\BrowserNotification':
                events.onBrowserNotificationReceived(notification)
                break
            case 'App\\Notifications\\AppSyncNotification':
                events.onAppSyncNotificationReceived(notification)
                break
        }
    })
}

//  Initialize the Tenant Notifications channel
const tenantInit = (tenant: Tenant) => {
    store.getters.appDebug && console.log('WEBSOCKETS: Initializing Tenant Channel')

    //  Set the Tenant's channel attributes
    tenantChannel = {
        model: 'App.SnapDesk.Tenant',
        id: tenant.id,
        timestamp: moment.utc(tenant.created_at).unix()
    }

    //  Convert ChannelProps in the dot notated string
    const channelStr = _getChannelName(tenantChannel)

    //  Check if we have already subscribed to the channels (this prevents duplicates that cause multiple events!)
    const hasTenantChannel = `private-${channelStr}` in window.Echo.connector.channels

    // Subscribe to the Tenant's channel This channel handles tenant state events. (eg data-change-events)
    if (!hasTenantChannel) {
        window.Echo.private(channelStr).notification((notification) => {
            store.getters.appDebug && console.log('WEBSOCKET: AppSync Notification Received:')

            switch (notification.type) {
                case 'App\\Notifications\\TenantNotification':
                    events.onAppSyncNotificationReceived(notification)
                    break
            }
        })
    }
}

//  Convert a channel object, into a dot notated string
const _getChannelName = (obj: ChannelProps) => {
    return Object.values(obj).join('.')
}


//  Handle the 'connection failed' event
const onConnectionFailed = () => {
    console.log('PUSHER: onConnectionFailed')

    //  Get the tenant and user (we need them to resubscribe to the pusher channels)
    const user = store.getters.user
    const tenant = store.getters.tenant

    //  Unsubscribe from failed channels
    resetUser()
    resetTenant()

    //  Reconnect the pusher channels
    if (user) userInit(user)
    if (tenant) tenantInit(tenant)
}
//  Handle the 'reconnecting connection' event
const onReconnectedConnection = () => {
    //  Get the current timestamp
    const now = Date.now()

    //  Get the last connection time
    const lastConnected = store.getters.websocketsConnected

    //  Determine how much time has passed between now and last connection
    const diff = now - lastConnected

    //  We should reload data if the client was disconnected for 5 seconds
    const shouldLoad = diff > 5000

    console.log('PUSHER: onReconnectedConnection', now, lastConnected, diff, shouldLoad)

    //  Else, reload the dataset
    if (shouldLoad) AppSync.onWebsocketsReconnected()
}

//  Handle the 'new connection' event
const onNewConnection = () => {
    //  Update the 'connected' timestamp (we use this is determine time between new connections)
    store.commit('websocketsConnectedSet', Date.now())
}

//  Unsubscribe from a Tenant channel
const resetTenant = () => {
    //  Check if there is an active Tenant channel
    const isActive = window.Echo && tenantChannel

    //  If the tenant channel is active, leave it
    if (isActive) window.Echo.leave(_getChannelName(tenantChannel))
}

//  Unsubscribe from a User channel
const resetUser = () => {
    //  Check if there is an active User channel
    const isActive = window.Echo && userChannel

    //  If Echo exists, leave and destroy User channel
    if (isActive) window.Echo.leave(_getChannelName(userChannel))
}

//  Export public functions
export default {
    //  Handles the user logout event
    onUserLogout() {
        store.getters.appDebug && console.log('WEBSOCKETS: onUserLogout')

        //  If we don't have a connection, do nothing
        if (!window.Echo) return

        //  We actually want to disconnect from Pusher since unauthenticated users shouldn't be connected
        window.Echo.disconnect()
    },
    //  Handles the TenantRemoved event
    onTenantRemoved(tenant: Tenant): void {
        store.getters.appDebug && console.log('WEBSOCKETS: onTenantRemoved')

        //  Reset the tenant's channel
        resetTenant()
    },
    //  Handles the TenantSet event
    onTenantSet(tenant: Tenant): void {
        store.getters.appDebug && console.log('WEBSOCKETS: onTenantSet')

        //  Check if we are already connected to Pusher
        const exists = window.Echo && window.Pusher

        //  We're connected if we aren't disconnected and Echo exists
        const connected = exists && window.Echo.connector.pusher.connection.state !== 'disconnected'

        //  If we are NOT then initialize our connection
        if (!connected) init()

        //  Grab token from store
        const token = store.getters.token

        //  Set the Laravel Echo Authorization headers & Auth endpoint
        window.Echo.connector.pusher.config.auth.headers['Authorization'] = `Bearer ${token}`

        //  Grab user from store
        const user = store.getters.user

        //  Check if there is an active User channel
        const isActive = window.Echo && userChannel

        //  If there is not, then initialize one
        if (!isActive) userInit(user)

        //  Reset the tenant's channel
        resetTenant()

        //  Initialize the new tenant's websocket channel
        tenantInit(tenant)
    }
}
